import {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useDispatch } from "react-redux";
import { gxUserviewApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import { growxsettings } from "@/app/apiConfig/services/landingAbstactApiLayer";
import { setSettings, clearSettings } from "@/app/redux/reducers/userData";
import { useToken } from "./TokenContext";
import type { UserView } from "@/app/types/userprofile";

/* ---------- TYPES ---------- */

export type UserSettingData = {
  userId?: number;
  routingTypeCode?: string;
  userType?: string;
  userRegisterType?: string;
  isBankConnect?: string;
  recordStatusCode?: string | null;
  isAccountCheck?: string;
  isConsentCheck?: string;
  isKycVerified?: string;
  lfiHolder?: string;
  [key: string]: unknown;
};

export type ConsentData = {
  consentStatus?: "ACTIVE" | "INACTIVE";
  isConsentChecked?: "Y" | "N";
  lfiHolder?: string;
};

type UserDetailsContextType = {
  personalInfo: UserView | null;
  personalInfoLoading: boolean;
  fetchPersonalInfo: () => Promise<void>;
  userData: UserSettingData | null;
  userDataLoading: boolean;
  consentData: ConsentData | null;
  setConsentData: (data: ConsentData | null) => void;
};

const UserDetailsContext = createContext<UserDetailsContextType | undefined>(
  undefined,
);

export function UserDetailsProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch();
  const { token } = useToken();
  const [personalInfo, setPersonalInfo] = useState<UserView | null>(null);
  const [personalInfoLoading, setPersonalInfoLoading] = useState(false);
  const [userData, setUserDataState] = useState<UserSettingData | null>(null);
  const [userDataLoading, setUserDataLoading] = useState(false);
  const [consentData, setConsentDataState] = useState<ConsentData | null>(null);

  const setConsentData = useCallback((data: ConsentData | null) => {
    setConsentDataState(data);
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!token) return;

    setUserDataLoading(true);
    try {
      const settingsResponse = await growxsettings(token);
      const data = settingsResponse?.data;
      if (!data) return;

      const settingData: UserSettingData = {
        ...data.appSetting,
        ...data.userSetting,
      };

      setUserDataState(settingData);
      dispatch(setSettings(settingData));
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setUserDataLoading(false);
    }
  }, [token, dispatch]);

  /* ---------- FETCH PERSONAL INFO ---------- */

  const fetchPersonalInfo = useCallback(async () => {
    if (!userData?.userId) return;

    setPersonalInfoLoading(true);
    try {
      const response = await gxUserviewApi({ userId: userData.userId });
      if (response?.data) {
        setPersonalInfo({ ...response.data });
      }
    } catch (error) {
      console.error("Error fetching personal info:", error);
    } finally {
      setPersonalInfoLoading(false);
    }
  }, [userData?.userId]);

  useEffect(() => {
    if (token && !userData) {
      fetchUserData();
    }
  }, [token]);

  useEffect(() => {
    if (userData?.userId) {
      fetchPersonalInfo();
    }
  }, [userData?.userId]);

  useEffect(() => {
    if (!token) {
      setUserDataState(null);
      setPersonalInfo(null);
      setConsentDataState(null);
      dispatch(clearSettings());
    }
  }, [token]);

  const value = useMemo(
    () => ({
      personalInfo,
      personalInfoLoading,
      fetchPersonalInfo,
      userData,
      userDataLoading,
      consentData,
      setConsentData,
    }),
    [
      personalInfo,
      personalInfoLoading,
      fetchPersonalInfo,
      userData,
      userDataLoading,
      consentData,
      setConsentData,
    ],
  );

  return (
    <UserDetailsContext.Provider value={value}>
      {children}
    </UserDetailsContext.Provider>
  );
}

export function useUserDetails() {
  const context = useContext(UserDetailsContext);
  if (!context)
    throw new Error("useUserDetails must be used within a UserDetailsProvider");
  return context;
}
